import SectionHeader from '../../../components/SectionHeader';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';
import FormUpload from '../../../components/formInputs/FormUpload';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import { getMedia } from '../../../utils/HelperFunctions';
import { IApplication } from '../types/interfaces';
import Button from '../../../components/buttons/Button';
import ModalContainer from '../../../components/ModalContainer';
import ConfirmModal from '../../../components/ConfirmModal';
import { useContext, useState } from 'react';
import { generalCreate } from '../../../API/api';
import { authContext } from '../../../store/context/authContext';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

export default function SingleFinancingDetails({ data }: { data: IApplication }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const { catchError } = useContext(authContext);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const queryClient = useQueryClient()

  const [confirmationModal, setConfirmationModal] = useState<'confirm' | 'reject' | null>(null);

  const sectionsData = [
    {
      name: t('main_information'),
      rightComp: (
        <span className={`badge ${data?.status === 'Accepted' ? 'completed' : data?.status === 'Proccessing' ? 'current' : data?.status === 'Pending' ? 'pending' : 'cancelled'}`}>
          {data?.status}
        </span>
      ),
      items: [
        { title: t('order_id'), value: data?.id },
        { title: t('application_number'), value: data?.application_no },
        { title: t('model'), value: data?.variant?.vehicle_model?.name },
        { title: t('variant'), value: data?.variant?.display_name },
        { title: t('down_payment'), value: data?.down_payment?.toLocaleString('en-US') }
      ]
    },
    {
      name: t('customer_information'),
      items: [
        { title: t('first_name'), value: data?.user?.first_name },
        { title: t('last_name'), value: data?.user?.last_name },
        { title: t('country'), value: data?.user?.address?.country },
        { title: t('city'), value: data?.user?.address?.city },
        { title: t('address'), value: data?.user?.address?.street },
        { title: t('email'), value: data?.user?.email },
        { title: t('mobile'), value: data?.user?.mobile },
        { title: t('salary_transferred_to'), value: data?.salary_transfer_to },
        
      ]
    },
    {
      name: t('vehicle_details'),
      items: [
        { title: t('brand'), value: data?.variant?.vehicle_model?.vehicle_make?.name },
        { title: t('model'), value: data?.variant?.vehicle_model?.name },
        { title: t('variant'), value: data?.variant?.display_name },
        { title: t('exterior_color'), value: data?.exterior_color?.display_name },
        { title: t('interior_color'), value: data?.interior_color?.display_name }
      ]
    }
  ];

  function handleStatusChange(status: 3 | 4, handleSuccess: () => void) {
    if (!id) return;
    if (status === 3) setAcceptLoading(true);
    else setRejectLoading(true);
    generalCreate({
      route: `/admin/finance-applications/${id}/status`,
      values: { status },
      method: 'put'
    })
      .then(() => {
        handleSuccess();
        const contentContainer = document.querySelector('.layout_content');
        contentContainer?.scrollTo({ top: 0, behavior: 'smooth' });
        setAcceptLoading(false);
        setRejectLoading(false);
        queryClient.invalidateQueries({ queryKey: ['financing-applications'] });
      })
      .catch((error) => catchError(error));
  }

  const identification = getMedia(data?.media, 'identification')?.url;
  const bankStatement = getMedia(data?.media, 'bank_statement')?.url;
  const hrLetter = getMedia(data?.media, 'hr_letter')?.url;

  return (
    <Fragment>
      {sectionsData?.map((section) => (
        <SectionHeader title={section?.name} rightComp={section?.rightComp}>
          <div className="order-details">
            {section?.items?.map((item, index) => (
              <div className="order-details_item" key={`order-details-item-${index}`}>
                {item?.title && (
                  <Fragment>
                    <span className="order-details_item_title">{item?.title}:</span>
                    <span className="order-details_item_value">{item?.value || '---'}</span>
                  </Fragment>
                )}
              </div>
            ))}
          </div>
        </SectionHeader>
      ))}
      <SectionHeader title={t('files')} customStyle={data?.status === "Pending" ? '' : 'last'}>
        <div className="inputs_group">
          <FieldWrapper>
            <FormUpload label={`${t('identification')}`} value={identification || ''} name="identification" file disabled />
          </FieldWrapper>
          <FieldWrapper>
            <FormUpload label={`${t('bank_statement')}`} value={bankStatement || ''} name="bank_statement" file disabled />
          </FieldWrapper>
          <FieldWrapper>
            <FormUpload label={`${t('hr_letter')}`} value={hrLetter || ''} name="hr_letter" file disabled />
          </FieldWrapper>
        </div>
      </SectionHeader>
      {data?.status === "Pending" && <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', justifyContent: 'flex-end' }}>
        <Button
          type="button"
          loading={acceptLoading}
          onClick={() => {
            setConfirmationModal('confirm');
          }}
        >
          <span className="bold">{t('accept')}</span>
        </Button>
        <Button
          type="button"
          customClass="red"
          loading={rejectLoading}
          onClick={() => {
            setConfirmationModal('reject');
          }}
        >
          <span className="bold">{t('reject')}</span>
        </Button>
      </div>}
      {confirmationModal && (
        <ModalContainer small>
          <ConfirmModal
            closeModal={() => setConfirmationModal(null)}
            queryKey={'orders'}
            message={confirmationModal === 'confirm' ? t('are_you_sure_you_want_to_confirm_this_application') : t('are_you_sure_you_want_to_reject_this_application')}
            confirmFunc={(handleSuccess) => handleStatusChange(confirmationModal === 'confirm' ? 3 : 4, handleSuccess)}
            successMsg={confirmationModal === 'confirm' ? t('application_confirmed') : t('application_rejected')}
          />
        </ModalContainer>
      )}
    </Fragment>
  );
}
